import React, { useState, useEffect } from 'react';
import { ref, onValue, push, update, remove, set } from 'firebase/database';
import { managementDb } from '../firebase';
import { toast } from 'react-toastify';
import './GalleryManagement.css';

const GalleryManagement = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    category: 'corporate',
    icon: 'fa-video',
    isActive: true,
    order: 0
  });

  // Categories and icons mapping
  const categories = [
    { value: 'corporate', label: 'Corporate', icon: 'fa-building' },
    { value: 'brands', label: 'Brands', icon: 'fa-shopping-bag' },
    { value: 'documentary', label: 'Documentary', icon: 'fa-film' },
    { value: 'adfilm', label: 'Ad Films', icon: 'fa-video' },
    { value: 'performance-marketing', label: 'Performance Marketing', icon: 'fa-clapperboard' },
    { value: 'production', label: 'Production', icon: 'fa-camera' }
  ];

  // YouTube video ID extraction and URL conversion
  const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return '';
    
    let videoId = '';
    let convertedUrl = url;
    
    // Handle YouTube Shorts URLs
    if (url.includes('/shorts/')) {
      const shortsMatch = url.match(/\/shorts\/([^/?]+)/);
      if (shortsMatch && shortsMatch[1]) {
        videoId = shortsMatch[1];
        // Convert Shorts URL to regular YouTube URL
        convertedUrl = `https://www.youtube.com/watch?v=${videoId}`;
      }
    } else {
      // Handle regular YouTube URLs
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        videoId = match[2];
      }
    }
    
    return { videoId, convertedUrl };
  };

  // Fetch gallery items from Firebase - only metadata, not full video data
  useEffect(() => {
    const galleryRef = ref(managementDb, 'gallery');
    const unsubscribe = onValue(galleryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Only load basic metadata, thumbnails will be lazy loaded
        const items = Object.keys(data).map(id => ({
          id,
          ...data[id]
        })).sort((a, b) => a.order - b.order);
        
        setGalleryItems(items);
      } else {
        setGalleryItems([]);
      }
    });

    return () => unsubscribe();
  }, []);

  // Filter items based on active filter and search term
  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle status filters
    if (activeFilter === 'active') {
      return item.isActive && matchesSearch;
    } else if (activeFilter === 'inactive') {
      return !item.isActive && matchesSearch;
    } else if (activeFilter === 'all') {
      // Only show active items in "All Items" filter
      return item.isActive && matchesSearch;
    } else {
      // Category filters - only show active items
      return item.isActive && item.category === activeFilter && matchesSearch;
    }
  });

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    // Add a class to the body to indicate a drag is in progress
    document.body.classList.add('dragging');
  };

  const handleDragEnd = () => {
    if (draggedItem) {
      document.body.classList.remove('dragging');
      setDraggedItem(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) {
      return;
    }

    // Create a new ordered list
    let newItems = [...galleryItems];
    const fromIndex = newItems.findIndex(item => item.id === draggedItem.id);
    const toIndex = newItems.findIndex(item => item.id === targetItem.id);

    // Remove the dragged item and insert it at the new position
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);

    // Update the local state
    setGalleryItems(newItems);
    
    // Update the order in Firebase
    updateGalleryOrderInFirebase(newItems);
  };
  
  const updateGalleryOrderInFirebase = async (items) => {
    const updates = {};
    items.forEach((item, index) => {
      updates[`/gallery/${item.id}/order`] = index;
    });

    try {
      await update(ref(managementDb), updates);
      toast.success('Gallery order updated successfully!');
    } catch (error) {
      console.error('Error updating gallery order:', error);
      toast.error('Failed to update gallery order');
      // Optionally, revert the local state if the update fails
    }
  };


  // Show all items without pagination
  const currentItems = filteredItems;

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      videoUrl: '',
      category: 'corporate',
      icon: 'fa-video',
      isActive: true,
      order: 0
    });
    setEditingItem(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.videoUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { videoId, convertedUrl } = getYouTubeVideoId(formData.videoUrl);
    if (!videoId) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    try {
      const galleryRef = ref(managementDb, 'gallery');
      
      if (editingItem) {
        // Update existing item
        await update(ref(managementDb, `gallery/${editingItem.id}`), {
          ...formData,
          videoUrl: convertedUrl, // Store the converted URL
          videoId,
          updatedAt: Date.now()
        });
        toast.success('Gallery item updated successfully!');
      } else {
        // Add new item
        const newItemRef = push(galleryRef);
        await set(newItemRef, {
          ...formData,
          videoUrl: convertedUrl, // Store the converted URL
          videoId,
          createdAt: Date.now()
        });
        toast.success('Gallery item added successfully!');
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving gallery item:', error);
      toast.error('Failed to save gallery item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      videoUrl: item.videoUrl,
      category: item.category,
      icon: item.icon,
      isActive: item.isActive,
      order: item.order
    });
    setShowAddForm(true);
  };

  const handleDelete = async (itemId) => {
    // Simple confirmation dialog
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      try {
        await remove(ref(managementDb, `gallery/${itemId}`));
        toast.success('Gallery item deleted successfully!');
      } catch (error) {
        console.error('Error deleting gallery item:', error);
        toast.error('Failed to delete gallery item');
      }
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await update(ref(managementDb, `gallery/${item.id}`), {
        isActive: !item.isActive,
        updatedAt: Date.now()
      });
      toast.success(`Item ${item.isActive ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      console.error('Error toggling item status:', error);
      toast.error('Failed to update item status');
    }
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.icon : 'fa-video';
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const handleVideoClick = (item) => {
    setSelectedVideo(item);
    setShowVideoPopup(true);
  };

  const handleCloseVideoPopup = () => {
    setShowVideoPopup(false);
    setSelectedVideo(null);
  };

  const handlePopupBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseVideoPopup();
    }
  };

  const handleFormPopupBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      resetForm();
    }
  };

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-images me-2"></i>
                Gallery Management
              </h3>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                <i className="fas fa-plus me-2"></i>
                Add New Item
              </button>
            </div>
            <div className="card-body">
              
              {/* Filter Section */}
              <div className="filter-section mb-4">
                <div className="row align-items-center">
                  {/* Search Bar */}
                  <div className="col-md-8 mb-3">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by title, description, or category..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setSearchTerm('')}
                          type="button"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* View Mode Toggle - Right Corner */}
                  <div className="col-md-4 mb-3 text-end">
                    <div className="view-mode-switch">
                      <div className="switch-container">
                        <input
                          type="checkbox"
                          id="viewModeToggle"
                          className="switch-input"
                          checked={viewMode === 'list'}
                          onChange={(e) => setViewMode(e.target.checked ? 'list' : 'grid')}
                        />
                        <label htmlFor="viewModeToggle" className="switch-slider">
                          <span className="switch-icon grid-icon">
                            <i className="fas fa-th-large"></i>
                          </span>
                          <span className="switch-icon list-icon">
                            <i className="fas fa-list"></i>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Category Filters */}
                <div className="category-filters d-flex gap-2 align-items-center flex-wrap">
                  <div className="dropdown-filter">
                    <select 
                      className="form-select"
                      style={{ minWidth: '200px', borderRadius: '20px', padding: '8px 15px', cursor: 'pointer', border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}
                      value={['active', 'inactive'].includes(activeFilter) ? 'all' : activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                    >
                      <option value="all">All Categories ({galleryItems.filter(item => item.isActive).length})</option>
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label} ({galleryItems.filter(item => item.isActive && item.category === cat.value).length})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Status Filters */}
                  <button
                    className={`filter-btn ${activeFilter === 'active' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('active')}
                  >
                    <i className="fas fa-check-circle me-1"></i>
                    Active ({galleryItems.filter(item => item.isActive).length})
                  </button>
                  <button
                    className={`filter-btn ${activeFilter === 'inactive' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('inactive')}
                  >
                    <i className="fas fa-pause-circle me-1"></i>
                    Deactivated ({galleryItems.filter(item => !item.isActive).length})
                  </button>
                </div>
                
                {/* Search Results Summary */}
                {searchTerm && (
                  <div className="search-results-summary">
                    <small className="text-muted">
                      <i className="fas fa-search me-1"></i>
                      Search results: {filteredItems.length} items found for "{searchTerm}"
                    </small>
                  </div>
                )}
              </div>

              {/* Gallery Items Display */}
              <div 
                className={viewMode === 'grid' ? 'gallery-grid' : 'gallery-list'}
                onDragOver={handleDragOver}
              >
                {currentItems.map((item) => {
                  // Ensure videoId exists, extract from videoUrl if missing
                  const { videoId } = item.videoId ? { videoId: item.videoId } : getYouTubeVideoId(item.videoUrl);
                  
                  return (
                    <div 
                      key={item.id} 
                      className={`gallery-item ${viewMode === 'grid' ? 'gallery-item-grid' : 'gallery-item-list'} ${draggedItem && draggedItem.id === item.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleDrop(e, item)}
                    >
                      {/* Grid View Thumbnail */}
                      {viewMode === 'grid' ? (
                        <div className="gallery-item-thumbnail" onClick={() => handleVideoClick(item)}>
                          {videoId ? (
                            <img 
                              src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                              alt={item.title}
                              loading="lazy"
                              onError={(e) => {
                                // Try hqdefault if mqdefault fails
                                if (e.target.src.includes('mqdefault')) {
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                } else if (e.target.src.includes('hqdefault')) {
                                  // Try sddefault if hqdefault fails
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
                                } else {
                                  // Final fallback to default
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
                                }
                              }}
                            />
                          ) : (
                            <div className="no-thumbnail">
                              <i className="fas fa-video fa-2x text-muted"></i>
                              <p className="text-muted mt-1">No thumbnail</p>
                            </div>
                          )}
                          <div className="gallery-item-overlay">
                            <div className="gallery-item-actions">
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(item);
                                }}
                                title="Edit"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(item.id);
                                }}
                                title="Delete"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                          <div className="gallery-item-status">
                            <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        /* List View Thumbnail - Smaller Size */
                        <div className="gallery-item-thumbnail-list" onClick={() => handleVideoClick(item)}>
                          {videoId ? (
                            <img 
                              src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                              alt={item.title}
                              loading="lazy"
                              onError={(e) => {
                                // Try hqdefault if mqdefault fails
                                if (e.target.src.includes('mqdefault')) {
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                } else if (e.target.src.includes('hqdefault')) {
                                  // Try sddefault if hqdefault fails
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
                                } else {
                                  // Final fallback to default
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
                                }
                              }}
                            />
                          ) : (
                            <div className="no-thumbnail-list">
                              <i className="fas fa-video text-muted"></i>
                            </div>
                          )}
                          <div className="gallery-item-status-list">
                            <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="gallery-item-content">
                        <div className="gallery-item-header">
                          <h6 className="gallery-item-title" onClick={() => handleVideoClick(item)}>
                            {item.title}
                          </h6>
                          <p className="gallery-item-category">
                            <i className={`fas ${getCategoryIcon(item.category)} me-1`}></i>
                            {getCategoryLabel(item.category)}
                          </p>
                        </div>
                        <p className="gallery-item-description">{item.description}</p>
                        <div className="gallery-item-footer">
                          <div className="gallery-item-actions-main">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleActive(item);
                              }}
                            >
                              {item.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item);
                              }}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item.id);
                              }}
                            >
                              <i className="fas fa-trash me-1"></i>
                              Delete
                            </button>
                          </div>
                          <span className="gallery-item-order">Order: {item.order}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* No Items Message */}
              {currentItems.length === 0 && (
                <div className="no-items-message text-center py-5">
                  <i className="fas fa-video fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No gallery items found</h5>
                  <p className="text-muted">
                    {searchTerm ? 'Try adjusting your search terms' : 'Add your first gallery item to get started'}
                  </p>
                  {!searchTerm && (
                    <button 
                      className="btn btn-primary mt-2"
                      onClick={() => setShowAddForm(true)}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add New Item
                    </button>
                  )}
                </div>
              )}


              {/* Summary */}
              <div className="summary-section mt-4">
                <div className="row">
                  <div className="col-md-6">
                    <p className="text-muted mb-0">
                      {searchTerm ? (
                        <>
                          <i className="fas fa-search me-1"></i>
                          Showing {filteredItems.length} search results
                        </>
                      ) : (
                        <>
                          <i className="fas fa-th-large me-1"></i>
                          Showing all {filteredItems.length} items
                        </>
                      )}
                    </p>
                  </div>
                  <div className="col-md-6 text-end">
                    <p className="text-muted mb-0">
                      <i className="fas fa-check-circle me-1"></i>
                      Active: {galleryItems.filter(item => item.isActive).length} | 
                      <i className="fas fa-video me-1 ms-2"></i>
                      Total: {galleryItems.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Form Popup */}
      {showAddForm && (
        <div className="form-popup-overlay" onClick={handleFormPopupBackdropClick}>
          <div className="form-popup-content">
            <div className="form-popup-header">
              <h4 className="form-popup-title">
                {editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}
              </h4>
              <button 
                className="form-popup-close"
                onClick={resetForm}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="form-popup-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Category</label>
                      <select
                        className="form-select"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">YouTube Video URL *</label>
                  <input
                    type="url"
                    className="form-control"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleInputChange}
                    placeholder="Paste YouTube video URL or Link here"
                    required
                  />
                  <small className="form-text text-muted">
                    Supports regular YouTube URLs and YouTube Shorts URLs
                  </small>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                  ></textarea>
                </div>
                
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Icon</label>
                      <select
                        className="form-select"
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                      >
                        <option value="fa-video">Video</option>
                        <option value="fa-building">Building</option>
                        <option value="fa-shopping-bag">Shopping Bag</option>
                        <option value="fa-film">Film</option>
                        <option value="fa-clapperboard">Clapperboard</option>
                        <option value="fa-utensils">Utensils</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <div className="form-check mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">
                          Active (Show on website)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex gap-2 justify-content-end">
                  <button type="submit" className="btn btn-primary">
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                  {editingItem && (
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this gallery item?')) {
                          handleDelete(editingItem.id);
                          resetForm();
                        }
                      }}
                    >
                      <i className="fas fa-trash me-2"></i>
                      Delete Item
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Video Popup Dialog */}
      {showVideoPopup && selectedVideo && (
        <div className="video-popup-overlay" onClick={handlePopupBackdropClick}>
          <div className="video-popup-content">
            <div className="video-popup-header">
              <h4 className="video-popup-title">{selectedVideo.title}</h4>
              <button 
                className="video-popup-close"
                onClick={handleCloseVideoPopup}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="video-popup-body">
              <div className="video-container">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.videoId || getYouTubeVideoId(selectedVideo.videoUrl).videoId}?autoplay=1`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-details">
                <p className="video-category">
                  <i className={`fas ${getCategoryIcon(selectedVideo.category)} me-2`}></i>
                  {getCategoryLabel(selectedVideo.category)}
                </p>
                <p className="video-description">{selectedVideo.description}</p>
                <div className="video-meta">
                  <span className="video-order">Order: {selectedVideo.order}</span>
                  <span className={`video-status ${selectedVideo.isActive ? 'active' : 'inactive'}`}>
                    {selectedVideo.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default GalleryManagement;